import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Button,
  Snackbar,
  Select,
  Option,
  Modal,
  ModalDialog,
  ModalClose,
  Sheet,
  IconButton,
  Tooltip,
  Switch,
  Textarea,
  Divider,
  Alert,
  Checkbox,
  Card,
  CircularProgress,
  Grid,
  CardContent,
} from '@mui/joy';
import { IconWrapper } from './IconWrapper';
import { FaChevronDown, FaPlus, FaEdit, FaTrash, FaCopy, FaSearch } from 'react-icons/fa';
import { directSupabase } from '../lib/supabaseClient';
import type { Database } from '../types/supabase';
import { useUnifiedPricing } from '../hooks/useUnifiedPricing';

type Supplier = Database['public']['Tables']['suppliers']['Row'];
type SupplierPrice = Database['public']['Tables']['supplier_prices']['Row'];
type GlassExtra = Database['public']['Tables']['glass_extras']['Row'];

type UnifiedPrice = SupplierPrice;

function isSupplierPrice(price: UnifiedPrice): price is SupplierPrice {
  return 'glass_type' in price;
}

function getMakeupFromPrice(price: UnifiedPrice): string {
  if (isSupplierPrice(price)) {
    return price.makeup || price.thickness || '';
  }
  return '';
}

type SupplierPriceInput = Omit<SupplierPrice, 'id' | 'created_at'> & {
  poa?: boolean;
};

type PricingEntry = {
  pricePerM2?: { [supplierId: string]: number };
  minArea?: number;
  poa?: boolean;
  weightPerM2?: number;
  leadTime?: number;
  notes?: string;
};

type ProductsStructure = {
  [thickness: string]: {
    types: { [type: string]: PricingEntry };
  };
};

type ExtraData = {
  flat?: number;
  perKg?: number;
  pricePerM2?: { [supplierId: string]: number };
  poa?: boolean;
  special?: string;
  notes?: string;
};

type PricingStructure = {
  products: {
    Single: ProductsStructure;
    Double: ProductsStructure;
  };
  extras: {
    [name: string]: ExtraData;
  };
};

type MissingPrice = {
  glass_type: string;
  makeup: string;
  thickness: string;
  supplier_name: string;
  supplier_id: string;
};

interface PriceManagementProps {
  suppliers: Supplier[];
  selectedSupplier: string;
  onSupplierChange: (supplierId: string) => void;
}

interface PriceFormData {
  supplier_id: string;
  glass_type: string;
  thickness: string;
  makeup: string;
  price_per_sqm: number;
  min_area: number | null;
  weight_per_m2: number | null;
  poa: boolean;
}

export const PriceManagement: React.FC<PriceManagementProps> = ({
  suppliers,
  selectedSupplier,
  onSupplierChange,
}) => {
  const { prices: unifiedPrices, isLoading, error, refresh, extras } = useUnifiedPricing(selectedSupplier);
  const [snack, setSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [showAddPrice, setShowAddPrice] = useState(false);
  const [editingPrice, setEditingPrice] = useState<SupplierPrice | null>(null);
  const [showAddExtra, setShowAddExtra] = useState(false);
  const [editingExtra, setEditingExtra] = useState<GlassExtra | null>(null);
  const [showAddMakeup, setShowAddMakeup] = useState(false);
  const [editingMakeup, setEditingMakeup] = useState<SupplierPrice | null>(null);
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [priceFormData, setPriceFormData] = useState<PriceFormData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedPrices, setSelectedPrices] = useState<Set<string>>(new Set());
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyTargetSupplier, setCopyTargetSupplier] = useState<string>('');
  const [makeups, setMakeups] = useState<{ name: string; thickness: string | null }[]>([]);
  const [glassTypes, setGlassTypes] = useState<{ name: string }[]>([]);
  const [glassExtras, setGlassExtras] = useState<GlassExtra[]>([]);
  const [supplierExtras, setSupplierExtras] = useState<{ id: string; extra_id: string | null; price: number | null }[]>([]);
  const [editingExtras, setEditingExtras] = useState<{ [extraId: string]: number | '' }>({});
  const [extrasDirty, setExtrasDirty] = useState(false);
  const [editingExtraId, setEditingExtraId] = useState<string | null>(null);
  const [editingExtraValue, setEditingExtraValue] = useState<string>('');
  const [savingExtra, setSavingExtra] = useState(false);
  const [missingPrices, setMissingPrices] = useState<MissingPrice[]>([]);
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [populatingAll, setPopulatingAll] = useState(false);
  const [showAddMakeupModal, setShowAddMakeupModal] = useState(false);
  const [newMakeup, setNewMakeup] = useState('');
  const [newThickness, setNewThickness] = useState('');
  const [addingMakeup, setAddingMakeup] = useState(false);
  const [glassKind, setGlassKind] = useState<'Single' | 'Double'>('Single');

  useEffect(() => {
    // Fetch all makeups and glass types from reference tables
    const fetchReferenceData = async () => {
      // Fetch glass makeups and glass types directly from Supabase
      const { data: makeupsData, error: makeupsError } = await directSupabase
        .from('glass_makeups')
        .select('makeup, thickness');
      
      if (makeupsError) {
        console.error('Error fetching glass makeups:', makeupsError);
      } else {
        // Transform the data to match the expected interface
        const transformedMakeups = (makeupsData || []).map(item => ({
          name: item.makeup,
          thickness: item.thickness
        }));
        setMakeups(transformedMakeups);
      }

      const { data: glassTypesData, error: glassTypesError } = await directSupabase
        .from('glass_types')
        .select('name');
      
      if (glassTypesError) {
        console.error('Error fetching glass types:', glassTypesError);
      } else {
        setGlassTypes(glassTypesData || []);
      }
    };
    fetchReferenceData();
  }, []);

  useEffect(() => {
    // Fetch glass extras and supplier extras from database
    const fetchExtras = async () => {
      const { data: glassExtrasData, error: glassExtrasError } = await directSupabase
        .from('glass_extras')
        .select('*')
        .order('name', { ascending: true });
      
      if (glassExtrasError) {
        console.error('Error fetching glass extras:', glassExtrasError);
      } else {
        setGlassExtras(glassExtrasData || []);
      }
      
      if (selectedSupplier) {
        const { data: supplierExtrasData, error: supplierExtrasError } = await directSupabase
          .from('supplier_extras')
          .select('*, glass_extras(name)')
          .eq('supplier_id', selectedSupplier);
        
        if (supplierExtrasError) {
          console.error('Error fetching supplier extras:', supplierExtrasError);
        } else {
          setSupplierExtras(supplierExtrasData || []);
        }
      } else {
        setSupplierExtras([]);
      }
    };
    fetchExtras();
  }, [selectedSupplier]);

  useEffect(() => {
    const initial: { [extraId: string]: number | '' } = {};
    glassExtras.forEach(extra => {
      const supplierExtra = supplierExtras.find(e => e.extra_id === extra.id);
      initial[extra.id] = supplierExtra && supplierExtra.price !== null && supplierExtra.price !== undefined ? supplierExtra.price : '';
    });
    setEditingExtras(initial);
    setExtrasDirty(false);
  }, [supplierExtras, glassExtras]);

  // Build a lookup for supplier prices (only real supplier prices, not fallback)
  const priceLookup = React.useMemo(() => {
    const map = new Map<string, number>();
    unifiedPrices
      .filter((row: any) => !('fallback' in row))
      .forEach((row: any) => {
        map.set(`${row.makeup}|${row.glass_type}`, row.price_per_sqm);
      });
    return map;
  }, [unifiedPrices]);

  const handleAddPrice = () => {
    if (!selectedSupplier) {
      setSnackMessage('Please select a supplier first');
      setSnack(true);
      return;
    }
    
    setGlassKind('Single');
    setPriceFormData({
      supplier_id: selectedSupplier,
      glass_type: '',
      thickness: '',
      makeup: '',
      price_per_sqm: 0,
      min_area: null,
      weight_per_m2: null,
      poa: false,
    });
    setShowPriceForm(true);
    setEditingPrice(null);
  };

  const handleEditPrice = (price: SupplierPrice) => {
    setGlassKind(price.makeup && price.makeup.includes('/') ? 'Double' : 'Single');
    setPriceFormData({
      supplier_id: price.supplier_id || '',
      glass_type: price.glass_type || '',
      thickness: price.thickness || '',
      makeup: price.makeup || '',
      price_per_sqm: price.price_per_sqm || 0,
      min_area: price.min_area,
      weight_per_m2: price.weight_per_m2,
      poa: price.poa || false,
    });
    setShowPriceForm(true);
    setEditingPrice(price);
  };

  const handleSavePrice = async (priceData: PriceFormData) => {
    try {
      if (glassKind === 'Single') {
        let thicknessValue = priceData.thickness.trim();
        if (/^\d+$/.test(thicknessValue)) {
          thicknessValue = thicknessValue + 'mm';
        }
        const existingPrice = unifiedPrices.find(row =>
          (row.makeup === thicknessValue || row.thickness === thicknessValue) &&
          row.glass_type === priceData.glass_type
        );
        if (existingPrice) {
          const { error } = await directSupabase
            .from('supplier_prices')
            .update({
              glass_type: priceData.glass_type,
              thickness: thicknessValue,
              makeup: thicknessValue,
              price_per_sqm: priceData.price_per_sqm,
              min_area: priceData.min_area,
              weight_per_m2: priceData.weight_per_m2,
              poa: priceData.poa,
            })
            .eq('id', existingPrice.id);
          
          if (error) throw error;
        } else {
          const { error } = await directSupabase
            .from('supplier_prices')
            .insert([{
              supplier_id: priceData.supplier_id,
              glass_type: priceData.glass_type,
              thickness: thicknessValue,
              makeup: thicknessValue,
              price_per_sqm: priceData.price_per_sqm,
              min_area: priceData.min_area,
              weight_per_m2: priceData.weight_per_m2,
              poa: priceData.poa,
            }]);
          
          if (error) throw error;
        }
      } else {
        const outerThickness = priceData.makeup.split('/')[0];
        const relatedMakeups = makeups.filter(m => m.name.startsWith(`${outerThickness}/`));
        for (const makeup of relatedMakeups) {
          const existingPrice = unifiedPrices.find(row =>
            row.makeup === makeup.name &&
            row.glass_type === priceData.glass_type
          );
          if (existingPrice) {
            const { error } = await directSupabase
              .from('supplier_prices')
              .update({
                glass_type: priceData.glass_type,
                thickness: makeup.thickness,
                makeup: makeup.name,
                price_per_sqm: priceData.price_per_sqm,
                min_area: priceData.min_area,
                weight_per_m2: priceData.weight_per_m2,
                poa: priceData.poa,
              })
              .eq('id', existingPrice.id);
            
            if (error) throw error;
          } else {
            const { error } = await directSupabase
              .from('supplier_prices')
              .insert([{
                supplier_id: priceData.supplier_id,
                glass_type: priceData.glass_type,
                thickness: makeup.thickness,
                makeup: makeup.name,
                price_per_sqm: priceData.price_per_sqm,
                min_area: priceData.min_area,
                weight_per_m2: priceData.weight_per_m2,
                poa: priceData.poa,
              }]);
            
            if (error) throw error;
          }
        }
      }
      refresh();
      setSnackMessage('Prices updated successfully');
      setSnack(true);
      setShowPriceForm(false);
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to save prices');
      setSnack(true);
    }
  };

  const handleDeletePrice = async (priceId: string) => {
    try {
      const { error } = await directSupabase
        .from('supplier_prices')
        .delete()
        .eq('id', priceId);
      
      if (error) throw error;
      setSnackMessage('Price deleted successfully');
      refresh();
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to delete price');
    }
    setSnack(true);
  };

  const handleSaveExtra = async (extra: Omit<GlassExtra, 'id' | 'created_at'> & { type: string; poa?: boolean }) => {
    try {
      const extraData = {
        ...extra,
      };
      if (editingExtra) {
        const { error } = await directSupabase
          .from('glass_extras')
          .update(extraData)
          .eq('id', editingExtra.id);
        
        if (error) throw error;
        setSnackMessage('Extra updated successfully');
      } else {
        const { error } = await directSupabase
          .from('glass_extras')
          .insert([extraData]);
        
        if (error) throw error;
        setSnackMessage('Extra added successfully');
      }
      setShowAddExtra(false);
      setEditingExtra(null);
      refresh();
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to save extra');
    }
    setSnack(true);
  };

  const handleDeleteExtra = async (extraId: string) => {
    try {
      const { error } = await directSupabase
        .from('glass_extras')
        .delete()
        .eq('id', extraId);
      
      if (error) throw error;
      setSnackMessage('Extra deleted successfully');
      refresh();
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to delete extra');
    }
    setSnack(true);
  };

  const handleSaveMakeup = async (makeup: Omit<SupplierPrice, 'id' | 'created_at'>) => {
    try {
      if (editingMakeup) {
        const { error } = await directSupabase
          .from('supplier_prices')
          .update(makeup)
          .eq('id', editingMakeup.id);
        
        if (error) throw error;
        setSnackMessage('Price updated successfully');
      } else {
        const { error } = await directSupabase
          .from('supplier_prices')
          .insert([makeup]);
        
        if (error) throw error;
        setSnackMessage('Price added successfully');
      }
      setShowAddMakeup(false);
      setEditingMakeup(null);
      refresh();
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to save price');
    }
    setSnack(true);
  };

  const handleDeleteMakeup = async (priceId: string) => {
    try {
      const { error } = await directSupabase
        .from('supplier_prices')
        .delete()
        .eq('id', priceId);
      
      if (error) throw error;
      setSnackMessage('Price deleted successfully');
      refresh();
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to delete price');
    }
    setSnack(true);
  };

  const handleCopyPrices = async () => {
    if (!selectedSupplier || !copyTargetSupplier) {
      setSnackMessage('Please select both source and target suppliers');
      setSnack(true);
      return;
    }
    
    try {
      // Copy supplier prices
      const { data: sourcePrices, error: sourceError } = await directSupabase
        .from('supplier_prices')
        .select('*')
        .eq('supplier_id', selectedSupplier);
      
      if (sourceError) throw sourceError;
      
      const { data: targetPrices, error: targetError } = await directSupabase
        .from('supplier_prices')
        .select('*')
        .eq('supplier_id', copyTargetSupplier);
      
      if (targetError) throw targetError;
      
      const targetPriceKeys = new Set((targetPrices || []).map((p: any) => `${p.makeup}|${p.glass_type}`));
      
      for (const price of sourcePrices || []) {
        if (!targetPriceKeys.has(`${price.makeup}|${price.glass_type}`)) {
          const { error } = await directSupabase
            .from('supplier_prices')
            .insert([{
              ...price,
              id: undefined,
              created_at: undefined,
              supplier_id: copyTargetSupplier,
            }]);
          
          if (error) throw error;
        }
      }
      
      // Copy supplier extras
      for (const extra of supplierExtras) {
        if (extra.extra_id && extra.price !== null) {
          const { error } = await directSupabase
            .from('supplier_extras')
            .insert([{
              supplier_id: copyTargetSupplier,
              extra_id: extra.extra_id,
              price: extra.price
            }]);
          
          if (error) throw error;
        }
      }
      
      // Refresh supplier extras for the target supplier
      if (copyTargetSupplier === selectedSupplier) {
        const { data: supplierExtrasData, error: extrasError } = await directSupabase
          .from('supplier_extras')
          .select('*, glass_extras(name)')
          .eq('supplier_id', selectedSupplier);
        
        if (extrasError) throw extrasError;
        setSupplierExtras(supplierExtrasData || []);
      }
      
      setSnackMessage('Prices and extras copied successfully');
      setShowCopyModal(false);
      setCopyTargetSupplier('');
      refresh();
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to copy prices');
    }
    setSnack(true);
  };

  const filteredPricing = React.useMemo(() => {
    if (!unifiedPrices || !searchQuery) return unifiedPrices;

    const query = searchQuery.toLowerCase();
    return unifiedPrices.filter((price) => {
      if (isSupplierPrice(price)) {
        const makeup = price.makeup || price.thickness || '';
        return (
          (price.glass_type?.toLowerCase().includes(query) || false) ||
          (makeup?.toLowerCase().includes(query) || false) ||
          (price.thickness?.toLowerCase().includes(query) || false)
        );
      }
      return false;
    });
  }, [unifiedPrices, searchQuery]);

  console.log('prices from useUnifiedPricing', unifiedPrices);

  const handleEditExtra = (extraId: string, value: string) => {
    setEditingExtras(prev => ({ ...prev, [extraId]: value === '' ? '' : Number(value) }));
    setExtrasDirty(true);
  };

  const handleSaveExtras = async () => {
    if (!selectedSupplier) {
      setSnackMessage('Please select a supplier first');
      setSnack(true);
      return;
    }
    
    for (const extra of glassExtras) {
      const value = editingExtras[extra.id];
      const supplierExtra = supplierExtras.find(e => e.extra_id === extra.id);
      if (value === '' || value === null) {
        // Delete if exists
        if (supplierExtra) {
          const { error } = await directSupabase
            .from('supplier_extras')
            .delete()
            .eq('id', supplierExtra.id);
          
          if (error) throw error;
        }
      } else {
        if (supplierExtra) {
          // Update
          const { error } = await directSupabase
            .from('supplier_extras')
            .update({ price: value as number })
            .eq('id', supplierExtra.id);
          
          if (error) throw error;
        } else {
          // Create
          const { error } = await directSupabase
            .from('supplier_extras')
            .insert([{
              supplier_id: selectedSupplier,
              extra_id: extra.id,
              price: value as number
            }]);
          
          if (error) throw error;
        }
      }
    }
    
    // Refresh from DB
    const { data: supplierExtrasData, error } = await directSupabase
      .from('supplier_extras')
      .select('*, glass_extras(name)')
      .eq('supplier_id', selectedSupplier);
    
    if (error) throw error;
    setSupplierExtras(supplierExtrasData || []);
    setExtrasDirty(false);
    setSnackMessage('Extras saved successfully');
    setSnack(true);
  };

  const handleCancelExtras = () => {
    const initial: { [extraId: string]: number | '' } = {};
    glassExtras.forEach(extra => {
      const supplierExtra = supplierExtras.find(e => e.extra_id === extra.id);
      initial[extra.id] = supplierExtra && supplierExtra.price !== null && supplierExtra.price !== undefined ? supplierExtra.price : '';
    });
    setEditingExtras(initial);
    setExtrasDirty(false);
  };

  // Handler to open the edit dialog
  const handleOpenEditExtra = (extraId: string) => {
    const supplierExtra = supplierExtras.find(e => e.extra_id === extraId);
    setEditingExtraId(extraId);
    setEditingExtraValue(supplierExtra && supplierExtra.price !== null && supplierExtra.price !== undefined ? supplierExtra.price.toString() : '');
  };

  // Handler to save the extra
  const handleSaveEditExtra = async () => {
    if (!editingExtraId || !selectedSupplier) {
      setSnackMessage('Please select a supplier first');
      setSnack(true);
      return;
    }
    
    setSavingExtra(true);
    const value = editingExtraValue;
    const supplierExtra = supplierExtras.find(e => e.extra_id === editingExtraId);
    try {
      if (value === '' || value === null) {
        // Delete if exists
        if (supplierExtra) {
          const { error } = await directSupabase
            .from('supplier_extras')
            .delete()
            .eq('id', supplierExtra.id);
          
          if (error) throw error;
        }
      } else {
        const price = parseFloat(value);
        if (supplierExtra) {
          // Update
          const { error } = await directSupabase
            .from('supplier_extras')
            .update({ price })
            .eq('id', supplierExtra.id);
          
          if (error) throw error;
        } else {
          // Create
          const { error } = await directSupabase
            .from('supplier_extras')
            .insert([{
              supplier_id: selectedSupplier,
              extra_id: editingExtraId,
              price
            }]);
          
          if (error) throw error;
        }
      }
      
      // Always refresh after save
      const { data: supplierExtrasData, error } = await directSupabase
        .from('supplier_extras')
        .select('*, glass_extras(name)')
        .eq('supplier_id', selectedSupplier);
      
      if (error) throw error;
      setSupplierExtras(supplierExtrasData || []);
      setSnackMessage('Extra saved successfully');
      setSnack(true);
      setEditingExtraId(null);
      setEditingExtraValue('');
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to save extra');
      setSnack(true);
    } finally {
      setSavingExtra(false);
    }
  };

  // Handler to cancel editing
  const handleCancelEditExtra = () => {
    setEditingExtraId(null);
    setEditingExtraValue('');
  };

  // Add this helper function near the top of the file
  const groupMakeupsByThickness = (makeups: { name: string; thickness: string | null }[]) => {
    const groups = new Map<string, { name: string; thickness: string }[]>();
    
    // Filter out makeups with null thickness
    const validMakeups = makeups.filter(m => m.thickness !== null) as { name: string; thickness: string }[];
    
    validMakeups.forEach(makeup => {
      // Extract the outer thickness (first number in the makeup)
      const outerThickness = makeup.name.split('/')[0];
      const groupKey = `${outerThickness}/X/${outerThickness}`;
      
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)?.push(makeup);
    });
    
    return Array.from(groups.entries()).map(([groupName, makeups]) => ({
      groupName,
      makeups,
      thickness: makeups[0].thickness // All makeups in a group have the same thickness
    }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography>Loading pricing data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="danger">{error}</Typography>
      </Box>
    );
  }

  if (!unifiedPrices) {
    return (
      <Box p={2}>
        <Typography>No pricing data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Search and Actions Bar */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 3,
        alignItems: { xs: 'stretch', sm: 'center' }
      }}>
        <Input
          placeholder="Search prices..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          startDecorator={<IconWrapper icon={FaSearch} />}
          sx={{ 
            flex: 1,
            minWidth: { xs: '100%', sm: '200px' }
          }}
        />
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            startDecorator={<IconWrapper icon={FaPlus} />}
            onClick={handleAddPrice}
            fullWidth={false}
          >
            Add Price
          </Button>
          <Button
            variant="outlined"
            startDecorator={<IconWrapper icon={FaCopy} />}
            onClick={() => setShowCopyModal(true)}
            fullWidth={false}
          >
            Copy Prices
          </Button>
          <Button
            variant="outlined"
            color="neutral"
            onClick={() => setShowMissingModal(true)}
            fullWidth={false}
          >
            Missing Prices
          </Button>
        </Box>
      </Box>

      {/* Price Tables */}
      <Box sx={{ 
        width: '100%',
        overflowX: 'auto',
        '& > *': { minWidth: { xs: '600px', sm: 'auto' } }
      }}>
        <Stack spacing={3}>
          {/* Single Glass Prices */}
          <Card>
            <CardContent>
              <Typography level="title-lg" mb={2}>Single Glass Prices</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Thickness</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Type</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Price/m²</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Min Area</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Weight/m²</th>
                      <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>POA</th>
                      <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPricing.filter(p => p.makeup && !p.makeup.includes('/')).map((price) => (
                      <tr key={price.id}>
                        <td style={{ padding: '8px' }}>{price.thickness}</td>
                        <td style={{ padding: '8px' }}>{price.glass_type}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{price.price_per_sqm?.toFixed(2)}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{price.min_area ?? ''}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{price.weight_per_m2 ?? ''}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>{price.poa ? 'Yes' : 'No'}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <IconButton size="sm" onClick={() => handleEditPrice(price)}><IconWrapper icon={FaEdit} /></IconButton>
                          <IconButton size="sm" color="danger" onClick={() => handleDeletePrice(price.id)}><IconWrapper icon={FaTrash} /></IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </CardContent>
          </Card>

          {/* Double Glass Prices */}
          <Card>
            <CardContent>
              <Typography level="title-lg" mb={2}>Double Glass Prices</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Group</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Type</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Price/m²</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Min Area</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Weight/m²</th>
                      <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>POA</th>
                      <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupMakeupsByThickness(makeups).map(group => {
                      // For each group, get all unique glass types for that group
                      const groupPrices = filteredPricing.filter(p => group.makeups.some(m => m.name === p.makeup));
                      const glassTypesInGroup = Array.from(new Set(groupPrices.map(p => p.glass_type)));
                      return [
                        <tr key={group.groupName + '-header'}>
                          <td colSpan={7} style={{ fontWeight: 'bold', background: '#f7f7f7', padding: '8px' }}>{group.groupName}</td>
                        </tr>,
                        ...glassTypesInGroup.map(glassType => {
                          const price = groupPrices.find(p => p.glass_type === glassType);
                          return (
                            <tr key={group.groupName + '-' + glassType}>
                              <td style={{ padding: '8px' }}>{group.groupName}</td>
                              <td style={{ padding: '8px' }}>{glassType}</td>
                              <td style={{ padding: '8px', textAlign: 'right' }}>{price?.price_per_sqm?.toFixed(2) || ''}</td>
                              <td style={{ padding: '8px', textAlign: 'right' }}>{price?.min_area ?? ''}</td>
                              <td style={{ padding: '8px', textAlign: 'right' }}>{price?.weight_per_m2 ?? ''}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{price?.poa ? 'Yes' : 'No'}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                <IconButton size="sm" onClick={() => handleEditPrice(price!)} disabled={!price}><IconWrapper icon={FaEdit} /></IconButton>
                                <IconButton size="sm" color="danger" onClick={() => handleDeletePrice(price!.id)} disabled={!price}><IconWrapper icon={FaTrash} /></IconButton>
                              </td>
                            </tr>
                          );
                        })
                      ];
                    })}
                  </tbody>
                </table>
              </Box>
            </CardContent>
          </Card>

          {/* Extras */}
          <Card>
            <CardContent>
              <Typography level="title-lg" mb={2}>Extras</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Name</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Price</th>
                      <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {glassExtras.map((extra) => {
                      const supplierExtra = supplierExtras.find(e => e.extra_id === extra.id);
                      return (
                        <tr key={extra.id}>
                          <td style={{ padding: '8px' }}>{extra.name}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{supplierExtra && supplierExtra.price !== null ? supplierExtra.price.toFixed(2) : ''}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <IconButton size="sm" onClick={() => handleOpenEditExtra(extra.id)}><IconWrapper icon={FaEdit} /></IconButton>
                            <IconButton size="sm" color="danger" onClick={() => handleDeleteExtra(extra.id)}><IconWrapper icon={FaTrash} /></IconButton>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      {/* Add/Edit Price Modal */}
      <Modal
        open={showPriceForm}
        onClose={() => setShowPriceForm(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 }
        }}
      >
        <ModalDialog
          sx={{
            maxWidth: { xs: '100%', sm: '500px' },
            width: '100%',
            p: { xs: 2, sm: 3 }
          }}
        >
          <ModalClose />
          <Typography level="h4" mb={2}>
            {editingPrice ? 'Edit Price' : 'Add Price'}
          </Typography>
          <form onSubmit={(e: React.FormEvent<HTMLFormElement>) => { e.preventDefault(); handleSavePrice(priceFormData!); }}>
            <Stack spacing={2}>
              <FormControl required>
                <FormLabel>Glass Kind</FormLabel>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant={glassKind === 'Single' ? 'solid' : 'outlined'}
                    onClick={() => setGlassKind('Single')}
                    type="button"
                  >
                    Single Glass
                  </Button>
                  <Button
                    variant={glassKind === 'Double' ? 'solid' : 'outlined'}
                    onClick={() => setGlassKind('Double')}
                    type="button"
                  >
                    Double Glass
                  </Button>
                </Stack>
              </FormControl>
              <FormControl required>
                <FormLabel>Glass Type</FormLabel>
                <Input
                  value={priceFormData?.glass_type || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriceFormData(prev => prev ? { ...prev, glass_type: e.target.value } : prev)}
                  placeholder="e.g. Clear, Laminated"
                />
              </FormControl>
              <FormControl required>
                <FormLabel>Thickness</FormLabel>
                <Input
                  value={priceFormData?.thickness || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriceFormData(prev => prev ? { ...prev, thickness: e.target.value } : prev)}
                  placeholder="e.g. 4mm"
                />
              </FormControl>
              {glassKind === 'Double' && (
                <FormControl required>
                  <FormLabel>Makeup</FormLabel>
                  <Input
                    value={priceFormData?.makeup || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriceFormData(prev => prev ? { ...prev, makeup: e.target.value } : prev)}
                    placeholder="e.g. 4/4/4"
                  />
                </FormControl>
              )}
              <FormControl required>
                <FormLabel>Price per m² (£)</FormLabel>
                <Input
                  type="number"
                  value={priceFormData?.price_per_sqm ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriceFormData(prev => prev ? { ...prev, price_per_sqm: parseFloat(e.target.value) || 0 } : prev)}
                  slotProps={{ input: { step: '0.01' } }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Min Order Area (m²)</FormLabel>
                <Input
                  type="number"
                  value={priceFormData?.min_area ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriceFormData(prev => prev ? { ...prev, min_area: e.target.value ? parseFloat(e.target.value) : null } : prev)}
                  slotProps={{ input: { step: '0.01' } }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Weight per m² (kg)</FormLabel>
                <Input
                  type="number"
                  value={priceFormData?.weight_per_m2 ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriceFormData(prev => prev ? { ...prev, weight_per_m2: e.target.value ? parseFloat(e.target.value) : null } : prev)}
                  slotProps={{ input: { step: '0.01' } }}
                />
              </FormControl>
              <FormControl orientation="horizontal" sx={{ alignItems: 'center', gap: 2 }}>
                <FormLabel>POA</FormLabel>
                <Switch
                  checked={priceFormData?.poa || false}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriceFormData(prev => prev ? { ...prev, poa: e.target.checked } : prev)}
                />
              </FormControl>
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'flex-end',
                mt: 2
              }}>
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={() => setShowPriceForm(false)}
                  fullWidth={false}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  fullWidth={false}
                >
                  Save
                </Button>
              </Box>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>

      {/* Copy Prices Modal */}
      <Modal
        open={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 }
        }}
      >
        <ModalDialog
          sx={{
            maxWidth: { xs: '100%', sm: '500px' },
            width: '100%',
            p: { xs: 2, sm: 3 }
          }}
        >
          <ModalClose />
          <Typography level="h4" mb={2}>Copy Prices</Typography>
          <form onSubmit={(e: React.FormEvent<HTMLFormElement>) => { e.preventDefault(); handleCopyPrices(); }}>
            <Stack spacing={2}>
              <FormControl required>
                <FormLabel>Target Supplier</FormLabel>
                <Select
                  value={copyTargetSupplier}
                  onChange={(_, value) => setCopyTargetSupplier(value || '')}
                  placeholder="Select supplier"
                >
                  {suppliers.filter(s => s.id !== selectedSupplier && s.category === 'Glass').map(supplier => (
                    <Option key={supplier.id} value={supplier.id}>{supplier.supplier_name}</Option>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'flex-end',
                mt: 2
              }}>
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={() => setShowCopyModal(false)}
                  fullWidth={false}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  fullWidth={false}
                  disabled={!copyTargetSupplier}
                >
                  Copy
                </Button>
              </Box>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>

      {/* Missing Prices Modal */}
      <Modal
        open={showMissingModal}
        onClose={() => setShowMissingModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 }
        }}
      >
        <ModalDialog
          sx={{
            maxWidth: { xs: '100%', sm: '800px' },
            width: '100%',
            p: { xs: 2, sm: 3 }
          }}
        >
          <ModalClose />
          <Typography level="h4" mb={2}>Missing Prices</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Supplier</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Makeup</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Thickness</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--joy-palette-neutral-200)' }}>Glass Type</th>
                </tr>
              </thead>
              <tbody>
                {missingPrices.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '16px' }}>All permutations are present!</td></tr>
                ) : (
                  missingPrices.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '8px' }}>{item.supplier_name}</td>
                      <td style={{ padding: '8px' }}>{item.makeup}</td>
                      <td style={{ padding: '8px' }}>{item.thickness}</td>
                      <td style={{ padding: '8px' }}>{item.glass_type}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'flex-end',
            mt: 2
          }}>
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => setShowMissingModal(false)}
              fullWidth={false}
            >
              Close
            </Button>
            <Button
              color="primary"
              onClick={() => {/* ... */}}
              loading={populatingAll}
              fullWidth={false}
            >
              Populate All
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Edit Extra Price Modal */}
      <Modal
        open={!!editingExtraId}
        onClose={handleCancelEditExtra}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 }
        }}
      >
        <ModalDialog
          sx={{
            maxWidth: { xs: '100%', sm: '400px' },
            width: '100%',
            p: { xs: 2, sm: 3 }
          }}
        >
          <ModalClose />
          <Typography level="h4" mb={2}>Edit Extra Price</Typography>
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Price</FormLabel>
              <Input
                type="number"
                value={editingExtraValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingExtraValue(e.target.value)}
                slotProps={{ input: { step: '0.01' } }}
                autoFocus
              />
            </FormControl>
            <Box sx={{
              display: 'flex',
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end',
              mt: 2
            }}>
              <Button
                variant="outlined"
                color="neutral"
                onClick={handleCancelEditExtra}
                fullWidth={false}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEditExtra}
                loading={savingExtra}
                fullWidth={false}
              >
                Save
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Success Snackbar */}
      <Snackbar
        open={snack}
        autoHideDuration={3000}
        onClose={() => setSnack(false)}
        color="success"
        sx={{
          bottom: { xs: 0, sm: 24 },
          left: { xs: 0, sm: 24 },
          right: { xs: 0, sm: 'auto' },
          transform: { xs: 'none', sm: 'translateX(0)' }
        }}
      >
        {snackMessage}
      </Snackbar>
    </Box>
  );
}; 